/**
 * Consolidated Checkout Service
 * 
 * Single, streamlined checkout service that provides:
 * - Fast redirect to Shopify checkout (primary method)
 * - Comprehensive error handling and retry mechanisms
 * - Test product support for safe development
 * - Return handling from Shopify
 * - Analytics integration
 * - Network connectivity detection
 */

import { shopifyCartService } from './shopifyCartApi';
import { checkoutErrorHandler, CheckoutError } from './checkoutErrorHandler';
import { checkoutAnalyticsService } from './checkoutAnalyticsService';
import { debugError, debugGroup, debugLog, debugWarn } from '../utils/debugLog';
import { CartItem } from '../contexts/ShoppingCartContext';

const getStoredCartItemsForAnalytics = (): CartItem[] => {
  const storedCart = localStorage.getItem('eruption-radio-cart');
  if (!storedCart) {
    return [];
  }

  try {
    const parsed = JSON.parse(storedCart) as unknown;
    if (Array.isArray(parsed)) {
      return parsed as CartItem[];
    }
  } catch (parseError) {
    debugWarn('Failed to parse stored cart items for analytics tracking:', parseError);
  }

  return [];
};

export interface CheckoutOptions {
  useTestProducts?: boolean;
  returnUrl?: string;
}

export interface CheckoutResult {
  success: boolean;
  error?: string;
  checkoutUrl?: string;
  checkoutError?: CheckoutError;
  retryable?: boolean;
  suggestions?: string[];
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  checkoutErrors?: CheckoutError[];
}

/**
 * Consolidated Checkout Service Class
 */
class CheckoutService {
  private readonly TEST_PRODUCT_TAG = 'test-product';
  private readonly RETURN_URL_BASE = (() => {
    const override = process.env.REACT_APP_CHECKOUT_RETURN_URL_BASE?.trim();
    if (!override) {
      return window.location.origin;
    }

    try {
      const url = new URL(override);
      return `${url.protocol}//${url.host}`;
    } catch {
      return window.location.origin;
    }
  })();

  /**
   * Fast checkout redirect with minimal validation
   */
  async quickCheckout(options: CheckoutOptions = {}): Promise<CheckoutResult> {
    try {
      debugGroup('🚀 Quick checkout initiated', () => debugLog(options));
      
      // Track checkout start
      await shopifyCartService.getCart();
      const analyticsItems = getStoredCartItemsForAnalytics();
      if (analyticsItems.length > 0) {
        checkoutAnalyticsService.trackBeginCheckout(analyticsItems);
        checkoutAnalyticsService.trackCheckoutInitiation(analyticsItems);
      } else {
        debugWarn('Checkout analytics fallback: no stored cart items available for tracking.');
      }

      // Get checkout URL quickly
      const checkoutUrl = await this.getCheckoutUrlFast();
      if (!checkoutUrl) {
        throw new Error('Unable to generate checkout URL');
      }

      // Add return URL parameter
      const enhancedUrl = this.addReturnUrl(checkoutUrl, options.returnUrl);

      // Direct redirect to Shopify checkout
      debugLog('🔗 Redirecting to Shopify checkout:', enhancedUrl);
      try {
        localStorage.setItem('eruption-radio-checkout-original-url', checkoutUrl);
        localStorage.setItem('eruption-radio-checkout-redirect-url', enhancedUrl);
      } catch {
        // no-op: storage may be unavailable
      }
      window.location.href = enhancedUrl;
      
      return {
        success: true,
        checkoutUrl: enhancedUrl
      };

    } catch (error) {
      debugError('❌ Quick checkout failed:', error);
      
      // Process error with enhanced error handler
      const checkoutError = checkoutErrorHandler.processError(
        error instanceof Error ? error : new Error(String(error)), 
        { context: 'quickCheckout', timestamp: new Date().toISOString() }
      );

      // Track error
      checkoutAnalyticsService.trackCheckoutError(
        checkoutError.category,
        checkoutError.message,
        'quick_checkout'
      );

      return {
        success: false,
        error: checkoutError.userMessage,
        checkoutError,
        retryable: checkoutError.retryable,
        suggestions: checkoutError.suggestions
      };
    }
  }

  /**
   * Comprehensive checkout with full validation and error handling
   */
  async redirectToCheckout(): Promise<void> {
    try {
      debugLog('Starting comprehensive checkout redirect...');
      
      // Use error handler with retry logic for the entire redirect process
      await checkoutErrorHandler.executeWithRetry(async () => {
        const result = await this.generateCheckoutUrl();
        
        if (!result.success || !result.checkoutUrl) {
          // If we have a specific checkout error, throw it with context
          if (result.checkoutError) {
            throw new Error(result.checkoutError.message);
          }
          throw new Error(result.error || 'Failed to generate checkout URL');
        }

        debugLog('Redirecting to:', result.checkoutUrl);

        // Validate URL before redirecting
        try {
          new URL(result.checkoutUrl);
        } catch (urlError) {
          throw new Error('Invalid checkout URL received from Shopify');
        }

        // Track checkout redirect
        checkoutAnalyticsService.trackCheckoutInitiation([]);

        // Redirect to checkout
        window.location.href = result.checkoutUrl;
      }, {
        operation: 'redirectToCheckout'
      });

    } catch (error) {
      debugError('Failed to redirect to checkout:', error);
      
      const checkoutError = checkoutErrorHandler.processError(
        error instanceof Error ? error : new Error(String(error)), 
        { operation: 'redirectToCheckout' }
      );

      // Track error
      checkoutAnalyticsService.trackCheckoutError(
        checkoutError.category,
        checkoutError.message,
        'redirect_checkout'
      );

      // Re-throw with enhanced error information
      const enhancedError: Error & { checkoutError: CheckoutError } = Object.assign(
        new Error(checkoutError.userMessage),
        { checkoutError }
      );
      throw enhancedError;
    }
  }

  /**
   * Generate checkout URL with comprehensive validation
   */
  async generateCheckoutUrl(): Promise<CheckoutResult> {
    try {
      debugLog('=== CHECKOUT DEBUG START ===');
      
      // Use error handler with retry logic
      const result = await checkoutErrorHandler.executeWithRetry(async () => {
        // Check network connectivity first
        const networkStatus = checkoutErrorHandler.getNetworkStatus();
        if (!networkStatus.online) {
          throw new Error('No internet connection available');
        }

        // Check local cart state first
        const localCartData = localStorage.getItem('eruption-radio-cart');
      debugLog('Local cart data:', localCartData);
        
        const shopifyCartId = localStorage.getItem('shopify-cart-id');
        debugLog('Shopify cart ID:', shopifyCartId);
        
        // Get current cart from Shopify with timeout
        const cart = await shopifyCartService.getCart();
        debugLog('Shopify cart retrieved:', cart ? 'Found' : 'Not found');
        debugGroup('Shopify cart details', () => debugLog(cart));
        
        if (!cart) {
        debugLog('No Shopify cart found - attempting to create/sync cart...');
          
          // Try to create a new cart with current items
          const createResult = await this.createCartFromLocalItems();
          if (createResult.success) {
            return createResult.checkoutUrl!;
          }
          
          throw new Error('No active cart found. Please add items to your cart again.');
        }

        // Validate cart has items
        if (cart.totalQuantity === 0) {
          debugLog('Shopify cart is empty');
          throw new Error('Cart is empty');
        }

        debugLog('Cart has items:', cart.totalQuantity);
        
        // Validate checkout URL exists
        if (!cart.checkoutUrl) {
          throw new Error('Checkout URL not available from Shopify');
        }

        debugLog('=== CHECKOUT DEBUG END ===');
        return cart.checkoutUrl;
      }, {
        operation: 'generateCheckoutUrl',
        cartId: localStorage.getItem('shopify-cart-id')
      });

      return {
        success: true,
        checkoutUrl: result,
      };

    } catch (error) {
      debugError('Failed to generate checkout URL:', error);
      
      // Process error with enhanced error handler
      const checkoutError = checkoutErrorHandler.processError(
        error instanceof Error ? error : new Error(String(error)), 
        { operation: 'generateCheckoutUrl' }
      );
      
      return {
        success: false,
        error: checkoutError.userMessage,
        checkoutError,
        retryable: checkoutError.retryable,
        suggestions: checkoutError.suggestions,
      };
    }
  }

  /**
   * Validate checkout before proceeding
   */
  async validateCheckout(): Promise<ValidationResult> {
    try {
      debugLog('Validating checkout...');
      
      const errors: string[] = [];
      const checkoutErrors: CheckoutError[] = [];
      const localCartRaw = localStorage.getItem('eruption-radio-cart');
      let localCartItems: CartItem[] = [];

      if (localCartRaw) {
        try {
          const parsed = JSON.parse(localCartRaw);
          if (Array.isArray(parsed)) {
            localCartItems = parsed as CartItem[];
          }
        } catch (parseError) {
          debugWarn('Failed to parse local cart during validation:', parseError);
        }
      }

      // Network check is advisory only; avoid blocking checkout on unreliable offline signals
      const networkStatus = checkoutErrorHandler.getNetworkStatus();
      if (!networkStatus.online) {
        debugWarn('Checkout validation: navigator reports offline, proceeding to validate cart anyway.');
      }
      
      // Check if cart exists and has items locally before Shopify fetch
      if (localCartItems.length === 0) {
        const error = checkoutErrorHandler.processError(
          new Error('Cart is empty'), 
          { operation: 'validateCheckout' }
        );
        errors.push(error.userMessage);
        checkoutErrors.push(error);
      }

      // Check if cart exists and has items in Shopify (attempt to sync if missing)
      let cart = await shopifyCartService.getCart();
      if ((!cart || cart.totalQuantity === 0) && localCartItems.length > 0) {
        try {
          cart = await shopifyCartService.syncCart(localCartItems);
        } catch (syncError) {
          const error = checkoutErrorHandler.processError(
            syncError instanceof Error ? syncError : new Error(String(syncError)),
            { operation: 'validateCheckout' }
          );
          errors.push(error.userMessage);
          checkoutErrors.push(error);
        }
      }

      if (!cart || cart.totalQuantity === 0) {
        const error = checkoutErrorHandler.processError(
          new Error('Cart is empty'), 
          { operation: 'validateCheckout' }
        );
        errors.push(error.userMessage);
        checkoutErrors.push(error);
      }

      // Check if checkout URL is available
      if (cart && !cart.checkoutUrl) {
        const error = checkoutErrorHandler.processError(
          new Error('Checkout URL not available'), 
          { operation: 'validateCheckout' }
        );
        errors.push(error.userMessage);
        checkoutErrors.push(error);
      }

      return {
        valid: errors.length === 0,
        errors,
        checkoutErrors
      };

    } catch (error) {
      debugError('Validation failed:', error);
      
      const checkoutError = checkoutErrorHandler.processError(
        error instanceof Error ? error : new Error(String(error)), 
        { operation: 'validateCheckout' }
      );

      return {
        valid: false,
        errors: [checkoutError.userMessage],
        checkoutErrors: [checkoutError]
      };
    }
  }

  /**
   * Get checkout URL with proper cart synchronization
   */
  private async getCheckoutUrlFast(): Promise<string | null> {
    try {
      // Get local cart data
      const localCartData = localStorage.getItem('eruption-radio-cart');
      if (!localCartData) {
        throw new Error('No items in cart');
      }

      let localItems;
      try {
        localItems = JSON.parse(localCartData);
        if (!localItems || localItems.length === 0) {
          throw new Error('Cart is empty');
        }
    } catch (parseError) {
      debugError('Failed to parse local cart data:', parseError);
        throw new Error('Invalid cart data in localStorage');
      }

      // Transform CartItemData[] to CartItem[] format expected by syncCart
      const cartItems = localItems.map((item: any) => ({
        id: item.id || item.variantId,
        productId: item.productId || '',
        variantId: item.variantId,
        product: item.product || { 
          id: item.productId || '',
          title: item.productTitle || item.title || 'Unknown Product',
          handle: item.handle || '',
          tags: []
        },
        variant: item.variant || {
          id: item.variantId,
          title: item.title || 'Default Title',
          price: item.price || { amount: '0.00', currencyCode: 'GBP' },
          availableForSale: item.availableForSale !== false
        },
        quantity: item.quantity || 1,
        selectedOptions: item.selectedOptions || {},
        addedAt: item.addedAt || new Date().toISOString(),
        updatedAt: item.updatedAt || new Date().toISOString()
      }));
      
      debugLog('🔄 Syncing cart with Shopify:', cartItems.length, 'items');
      
      // Use syncCart instead of createCart - this will update existing cart or create new one
      const cart = await shopifyCartService.syncCart(cartItems);

      if (!cart || !cart.checkoutUrl) {
        throw new Error('No valid cart or checkout URL available');
      }

      debugLog('✅ Cart synced successfully, checkout URL:', cart.checkoutUrl);
      return cart.checkoutUrl;
    } catch (error) {
      debugError('Failed to get checkout URL:', error);
      return null;
    }
  }

  /**
   * Create Shopify cart from local cart items
   */
  private async createCartFromLocalItems(): Promise<CheckoutResult> {
    try {
      debugLog('Attempting to create cart from local items...');
      
      // Get local cart items
      const localCartData = localStorage.getItem('eruption-radio-cart');
      if (!localCartData) {
        debugLog('No local cart data found');
        return { success: false, error: 'No local cart data' };
      }

      const localCart = JSON.parse(localCartData);
      debugGroup('Local cart parsed', () => debugLog(localCart));

      if (!localCart || localCart.length === 0) {
        debugLog('No items in local cart');
        return { success: false, error: 'No items in local cart' };
      }

      debugGroup('Local cart items', () => debugLog(localCart));

      // Create new Shopify cart
      const newCart = await shopifyCartService.createCart(localCart);
      debugGroup('New Shopify cart created', () => debugLog(newCart));

      if (newCart && newCart.checkoutUrl) {
        return {
          success: true,
          checkoutUrl: newCart.checkoutUrl,
        };
      }

      return { success: false, error: 'Failed to create cart' };
    } catch (error) {
      debugError('Failed to create cart from local items:', error);
      return { success: false, error: 'Failed to create cart from local items' };
    }
  }

  /**
   * Add return URL to checkout URL with multiple methods for better compatibility
   */
  private addReturnUrl(checkoutUrl: string, customReturnUrl?: string): string {
    try {
      const url = new URL(checkoutUrl);
      const configuredReturnUrl = process.env.REACT_APP_CHECKOUT_RETURN_URL?.trim();
      const returnUrl =
        customReturnUrl ||
        configuredReturnUrl ||
        `${this.RETURN_URL_BASE}/store?checkout=complete`;

      // Standard return URL parameter used by Shopify checkout
      url.searchParams.set('return_url', returnUrl);
      // Extra compatibility for cart-permalink -> checkout redirects
      url.searchParams.set('checkout[return_url]', returnUrl);
      url.searchParams.set('return_to', returnUrl);
      url.searchParams.set('success_url', returnUrl);
      
      debugGroup('🔗 Enhanced checkout URL with return URLs', () => debugLog({
        originalUrl: checkoutUrl,
        returnUrl: returnUrl,
        enhancedUrl: url.toString()
      }));
      
      return url.toString();
    } catch (error) {
      debugLog('Failed to add return URL:', error);
      return checkoutUrl;
    }
  }

  /**
   * Handle return from checkout
   */
  handleCheckoutReturn(completed: boolean = false): void {
    debugLog('🔄 Handling checkout return, completed:', completed);
    
    if (completed) {
      // Clear cart
      localStorage.removeItem('eruption-radio-cart');
      localStorage.removeItem('eruption-radio-checkout-url');
      localStorage.removeItem('eruption-radio-shopify-cart-id');
      localStorage.removeItem('shopify-cart-id'); // legacy key (older builds)
      
      // Show success message
      this.showCheckoutSuccess();
      
      // Track completion
      debugLog('🎉 Checkout completed via return detection');
    } else {
      // User may have abandoned checkout
      debugLog('⚠️ Checkout return without completion detected');
    }

    // Refresh cart state
    window.dispatchEvent(new CustomEvent('cart-updated'));
  }

  /**
   * Show checkout success message
   */
  private showCheckoutSuccess(): void {
    // Create success notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #10b981;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      z-index: 10000;
      font-family: system-ui, -apple-system, sans-serif;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="font-size: 20px;">🎉</span>
        <span>Order completed successfully!</span>
      </div>
    `;

    document.body.appendChild(notification);

    // Remove after 5 seconds
    const timeoutId = window.setTimeout(() => {
      notification.remove();
      window.clearTimeout(timeoutId);
    }, 5000);
  }

  /**
   * Filter products for testing (only show test products)
   */
  async filterTestProducts(products: any[]): Promise<any[]> {
    return products.filter(product => 
      product.tags?.includes(this.TEST_PRODUCT_TAG) ||
      product.title?.toLowerCase().includes('test') ||
      product.handle?.includes('test')
    );
  }

  /**
   * Check if we're in test mode
   */
  isTestMode(): boolean {
    return process.env.NODE_ENV === 'development' || 
           window.location.search.includes('test=true') ||
           localStorage.getItem('shopify-test-mode') === 'true';
  }

  /**
   * Enable test mode
   */
  enableTestMode(): void {
    localStorage.setItem('shopify-test-mode', 'true');
    debugLog('🧪 Test mode enabled - only test products will be shown');
  }

  /**
   * Disable test mode
   */
  disableTestMode(): void {
    localStorage.removeItem('shopify-test-mode');
    debugLog('🏪 Test mode disabled - all products will be shown');
  }
}

// Create and export singleton instance
export const checkoutService = new CheckoutService();
export default checkoutService;
